import { NotificationReceiverTypeValue } from '../models/notification-receiver-type';

/**
 * Reads and writes a receiver's review-notification subscriptions in the
 * notification API.
 *
 * A subscription is expressed as a rating filter: the star ratings, from 1 to
 * 5, that the receiver wants to hear about. An empty filter means the receiver
 * is not subscribed.
 */
export interface NotificationSettingsService {
  /**
   * Returns the rating filter the receiver is currently subscribed to, and
   * brings the API's channel set in line with `channelList` on the way.
   *
   * The synchronization adds every channel in `channelList` that the API does
   * not hold yet, and removes every channel the API holds that `channelList`
   * does not contain.
   *
   * @param channelList The channels the receiver currently has access to.
   * @param receiverId An email address, or an identity reference for a web receiver.
   * @param receiverType Selects the identifier format and the delivery channel.
   * @returns The subscribed star ratings, or an empty array when the receiver
   * is not registered, holds no products, or holds any product that is
   * disabled.
   */
  checkAndSynchClientStatus(
    channelList: string[],
    receiverId: string,
    receiverType: NotificationReceiverTypeValue
  ): Promise<number[]>;

  /**
   * Writes the receiver's subscription, registering the receiver first if the
   * API does not know it yet.
   *
   * A `ratingFilter` of every rating from 1 to 5 disables the subscription,
   * exactly as an empty filter does. Only a partial filter enables it.
   *
   * @param receiverId An email address, or an identity reference for a web receiver.
   * @param receiverType Selects the identifier format and the delivery channel.
   * @param channelList The channels to write the subscription for.
   * @param ratingFilter The star ratings to subscribe to, as values from 1 to 5.
   * @returns A promise that resolves once the write has succeeded.
   * @throws InvalidNotificationReceiverError When `receiverId` does not match
   * `receiverType`. The service issues no request in that case.
   * @throws Error When the notification API rejects the write.
   */
  updateOrCreateClient(
    receiverId: string,
    receiverType: NotificationReceiverTypeValue,
    channelList: string[],
    ratingFilter: number[]
  ): Promise<void>;
}
